/**
 * Copyright 2018 Phenix Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
define([
    'phenix-web-lodash-light',
    'phenix-web-assert',
    'phenix-web-disposable'
], function(_, assert, disposable) {
    'use strict';

    function ClosestEndPointResolver(options, onClosestEndpointFound, onAnEndpointResolved) {
        assert.isObject(options, 'options');
        assert.isObject(options.logger, 'options.logger');
        assert.isStringNotEmpty(options.version, 'options.version');
        assert.isFunction(onClosestEndpointFound, 'onClosestEndpointFound');

        if (onAnEndpointResolved) {
            assert.isFunction(onAnEndpointResolved, 'onAnEndpointResolved');
        }

        this._logger = options.logger;
        this._version = options.version;
        this._onClosestEndpointFound = onClosestEndpointFound;
        this._onAnEndpointResolved = onAnEndpointResolved;
        this._disposables = new disposable.DisposableList();
        this._done = false;
    }

    ClosestEndPointResolver.prototype.isResolved = function isResolved() {
        return this._done;
    };

    ClosestEndPointResolver.prototype.dispose = function dispose() {
        this._disposables.dispose();
    };

    ClosestEndPointResolver.prototype.completeCallback = function completeCallback(endPoint, time, data) {
        if (this.isResolved()) {
            return;
        }

        this._done = true;

        return this._onClosestEndpointFound(null, {
            uri: data,
            roundTripTime: time,
            endPoint: endPoint
        });
    };

    ClosestEndPointResolver.prototype.resolveAll = function resolveAll(endPoints) {
        for (var i = 0; i < endPoints.length; i++) {
            this.resolve(endPoints[i]);
        }
    };

    ClosestEndPointResolver.prototype.resolve = function resolve(endPoint) {
        var that = this;

        that._logger.info('Checking end point [%s]', endPoint);

        var version = that._version;
        var requestUrl = endPoint + '?version=' + version + '&_=' + _.now();
        var xhr = getAndOpenVendorSpecificXmlHttpMethod('GET', requestUrl);

        xhr.addEventListener('readystatechange', _.bind(handleReadyStateChange, this, xhr, function(err, response) {
            var time = _.get(response, ['stats', 'successResponseTime'], 0);

            if (that._onAnEndpointResolved) {
                if (err) {
                    that._onAnEndpointResolved(err, {endPoint: endPoint});
                } else {
                    that._onAnEndpointResolved(null, {
                        time: time,
                        endPoint: endPoint
                    });
                }
            }

            if (err) {
                if (err.code === 503) {
                    that._logger.info('End point [%s] is temporarily unavailable', endPoint);
                } else {
                    that._logger.warn('Unable to resolve end point [%s] with [%s]', endPoint, err);
                }

                return;
            } else if (that.isResolved()) {
                return;
            }

            return that.completeCallback(endPoint, time, response.data);
        }));
        xhr.timeout = 15000;
        xhr.send(null);
    };

    function getAndOpenVendorSpecificXmlHttpMethod(requestMethod, requestUrl) {
        var xhr = new XMLHttpRequest();

        if ('withCredentials' in xhr) {
            // Most browsers.
            xhr.open(requestMethod, requestUrl, true);
        } else if (typeof XDomainRequest !== 'undefined') {
            // IE8 & IE9
            // eslint-disable-next-line no-undef
            xhr = new XDomainRequest();
            xhr.open(requestMethod, requestUrl);
        } else {
            return;
        }

        return xhr;
    }

    function handleReadyStateChange(xhr, callback) {
        if (xhr.readyState === 4 /* DONE */) {
            if (xhr.status === 200) {
                var responseHeaders = getXhrResponseHeaders(xhr);
                var response = {
                    data: xhr.response || xhr.responseText,
                    headers: responseHeaders,
                    rawXhr: xhr
                };

                callback(null, response);
            } else {
                var err = new Error(xhr.status === 0 ? 'timeout' : 'Status=[' + xhr.status + ']');

                err.code = xhr.status;

                callback(err);
            }
        }
    }

    function getXhrResponseHeaders(xhr) {
        var responseHeadersString = xhr.getAllResponseHeaders();

        return _.reduce(responseHeadersString.trim().split(/[\r\n]+/), function(headers, header) {
            var parts = header.split(': ');
            var headerName = parts.shift();
            var headerValue = parts.join(': ');

            if (headerName) {
                headers[headerName] = headerValue;
            }

            return headers;
        }, {});
    }

    return ClosestEndPointResolver;
});